import React, { useState } from "react";
import { motion } from "framer-motion";
import NavBar from "@/components/NavBar";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Bell,
  MessageSquare,
  Heart,
  DollarSign,
  AlertCircle,
  CheckCircle,
  Inbox,
  CheckCheck,
  ArrowLeft,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  useUserNotifications,
  useMarkNotificationAsRead,
  useMarkAllNotificationsAsRead,
} from "@/hooks/useApi";

interface NotificationItemProps {
  notification: {
    id: string;
    type: string;
    message: string;
    campaignId?: string;
    read: boolean;
    timestamp: string;
    user?: {
      name: string;
      avatar: string;
    };
  };
  onClick: (id: string) => void;
}

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onClick,
}) => {
  const getIcon = () => {
    switch (notification.type) {
      case "comment":
        return (
          <MessageSquare className="text-primary w-4 h-4 sm:w-[18px] sm:h-[18px]" />
        );
      case "backed":
        return (
          <DollarSign className="text-primary w-4 h-4 sm:w-[18px] sm:h-[18px]" />
        );
      case "milestone":
        return (
          <CheckCircle className="text-primary w-4 h-4 sm:w-[18px] sm:h-[18px]" />
        );
      case "system":
        return (
          <AlertCircle className="text-primary w-4 h-4 sm:w-[18px] sm:h-[18px]" />
        );
      case "update":
        return (
          <Bell className="text-primary w-4 h-4 sm:w-[18px] sm:h-[18px]" />
        );
      case "like":
        return (
          <Heart className="text-primary w-4 h-4 sm:w-[18px] sm:h-[18px]" />
        );
      default:
        return (
          <Bell className="text-primary w-4 h-4 sm:w-[18px] sm:h-[18px]" />
        );
    }
  };

  return (
    <motion.div
      className={`p-3 sm:p-4 border-b last:border-b-0 cursor-pointer transition-all duration-200 hover:bg-muted/30 ${
        notification.read
          ? ""
          : "bg-gradient-to-r from-primary/5 to-primary/10 border-l-4 border-l-primary"
      }`}
      onClick={() => onClick(notification.id)}
      whileHover={{ scale: 1.01 }}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="flex items-start gap-3 sm:gap-4">
        {notification.user ? (
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-primary/20">
            <img
              src={notification.user.avatar}
              alt={notification.user.name}
              className="w-full h-full object-cover"
            />
          </div>
        ) : (
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-r from-primary to-primary-600 flex items-center justify-center flex-shrink-0">
            {getIcon()}
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 sm:gap-0 mb-2">
            <p className="text-xs sm:text-sm font-medium line-clamp-2">
              {notification.message}
            </p>
            {!notification.read && (
              <Badge className="self-start sm:ml-2 flex-shrink-0 bg-gradient-to-r from-primary to-primary-600 text-white border-0 text-xs">
                New
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {notification.timestamp}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

const UserNotifications: React.FC = () => {
  const [activeTab, setActiveTab] = useState("all");
  const navigate = useNavigate();

  const { data: notifications, isLoading, error } = useUserNotifications();
  const { mutate: markAsRead } = useMarkNotificationAsRead();
  const { mutate: markAllAsRead } = useMarkAllNotificationsAsRead();

  const unreadCount = notifications?.filter((n) => !n.read).length || 0;

  const handleMarkAsRead = (id: string) => {
    markAsRead(id);
  };

  const handleMarkAllAsRead = () => {
    markAllAsRead();
  };

  const filteredNotifications =
    notifications?.filter((notification) => {
      if (activeTab === "all") return true;
      if (activeTab === "unread") return !notification.read;
      return notification.type === activeTab;
    }) || [];

  if (isLoading) {
    return <div className="text-center py-12">Loading notifications...</div>;
  }

  if (error) {
    return (
      <div className="text-center py-12 text-red-500">{error.message}</div>
    );
  }

  if (!notifications || notifications.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-primary/5">
        <NavBar />
        <div className="container mx-auto px-4 pt-20 sm:pt-24 pb-16">
          <div className="max-w-4xl mx-auto">
            {/* Back Button */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Button
                variant="ghost"
                onClick={() => navigate(-1)}
                className="text-slate-600 hover:text-slate-900 hover:bg-slate-100"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <PageHeader
                title="Notifications"
                description="Stay updated with your latest activities and interactions"
              />
            </motion.div>

            <Card className="bg-white/95 backdrop-blur-sm border-0 shadow-2xl rounded-2xl overflow-hidden mt-6 sm:mt-8">
              <Tabs
                value={activeTab}
                onValueChange={setActiveTab}
                className="w-full"
              >
                <div className="bg-gradient-to-r from-gray-50 to-blue-50/50 border-b border-gray-100">
                  <TabsList className="w-full bg-transparent border-0 p-0 h-auto rounded-none overflow-x-auto">
                    <TabsTrigger
                      value="all"
                      className="flex-1 py-3 sm:py-4 lg:py-6 px-4 sm:px-6 lg:px-8 text-sm sm:text-base lg:text-lg font-semibold data-[state=active]:bg-white data-[state=active]:shadow-lg data-[state=active]:border-b-4 data-[state=active]:border-primary-500 rounded-none data-[state=active]:text-primary-600 whitespace-nowrap flex-shrink-0"
                    >
                      <Inbox className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                      All
                    </TabsTrigger>
                    <TabsTrigger
                      value="unread"
                      className="flex-1 py-3 sm:py-4 lg:py-6 px-4 sm:px-6 lg:px-8 text-sm sm:text-base lg:text-lg font-semibold data-[state=active]:bg-white data-[state=active]:shadow-lg data-[state=active]:border-b-4 data-[state=active]:border-primary-500 rounded-none data-[state=active]:text-primary-600 whitespace-nowrap flex-shrink-0"
                    >
                      <Bell className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                      Unread
                      {unreadCount > 0 && (
                        <Badge className="ml-1 sm:ml-2 bg-primary text-white border-0 text-xs">
                          {unreadCount}
                        </Badge>
                      )}
                    </TabsTrigger>
                    <TabsTrigger
                      value="comment"
                      className="flex-1 py-3 sm:py-4 lg:py-6 px-4 sm:px-6 lg:px-8 text-sm sm:text-base lg:text-lg font-semibold data-[state=active]:bg-white data-[state=active]:shadow-lg data-[state=active]:border-b-4 data-[state=active]:border-primary-500 rounded-none data-[state=active]:text-primary-600 whitespace-nowrap flex-shrink-0"
                    >
                      <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                      Comments
                    </TabsTrigger>
                    <TabsTrigger
                      value="system"
                      className="flex-1 py-3 sm:py-4 lg:py-6 px-4 sm:px-6 lg:px-8 text-sm sm:text-base lg:text-lg font-semibold data-[state=active]:bg-white data-[state=active]:shadow-lg data-[state=active]:border-b-4 data-[state=active]:border-primary-500 rounded-none data-[state=active]:text-primary-600 whitespace-nowrap flex-shrink-0"
                    >
                      <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                      System
                    </TabsTrigger>
                  </TabsList>
                </div>

                <TabsContent value="all" className="p-4 sm:p-6 lg:p-8">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
                    <div>
                      <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-primary to-primary-600 bg-clip-text text-transparent">
                        All Notifications
                      </h2>
                      <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                        {notifications?.length || 0} total notifications
                      </p>
                    </div>
                    {unreadCount > 0 && (
                      <Button
                        onClick={handleMarkAllAsRead}
                        variant="outline"
                        className="hover:bg-primary/10 hover:border-primary/20 text-xs sm:text-sm"
                      >
                        <CheckCheck className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                        <span className="hidden sm:inline">
                          Mark All as Read
                        </span>
                        <span className="sm:hidden">Mark All Read</span>
                      </Button>
                    )}
                  </div>

                  <div className="space-y-2">
                    {filteredNotifications.length === 0 ? (
                      <div className="text-center py-8 sm:py-12">
                        <Inbox className="w-12 h-12 sm:w-16 sm:h-16 text-muted-foreground mx-auto mb-3 sm:mb-4" />
                        <h3 className="text-base sm:text-lg font-semibold text-muted-foreground mb-2">
                          No notifications
                        </h3>
                        <p className="text-xs sm:text-sm text-muted-foreground">
                          You're all caught up! Check back later for new
                          updates.
                        </p>
                      </div>
                    ) : (
                      filteredNotifications.map((notification) => (
                        <NotificationItem
                          key={notification.id}
                          notification={notification}
                          onClick={handleMarkAsRead}
                        />
                      ))
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="unread" className="p-4 sm:p-6 lg:p-8">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
                    <div>
                      <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-primary to-primary-600 bg-clip-text text-transparent">
                        Unread Notifications
                      </h2>
                      <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                        {unreadCount} unread notifications
                      </p>
                    </div>
                    {unreadCount > 0 && (
                      <Button
                        onClick={handleMarkAllAsRead}
                        className="bg-primary hover:bg-primary/90 text-xs sm:text-sm"
                      >
                        <CheckCheck className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                        <span className="hidden sm:inline">
                          Mark All as Read
                        </span>
                        <span className="sm:hidden">Mark All Read</span>
                      </Button>
                    )}
                  </div>

                  <div className="space-y-2">
                    {filteredNotifications.length === 0 ? (
                      <div className="text-center py-8 sm:py-12">
                        <CheckCircle className="w-12 h-12 sm:w-16 sm:h-16 text-primary mx-auto mb-3 sm:mb-4" />
                        <h3 className="text-base sm:text-lg font-semibold text-muted-foreground mb-2">
                          All caught up!
                        </h3>
                        <p className="text-xs sm:text-sm text-muted-foreground">
                          You have no unread notifications.
                        </p>
                      </div>
                    ) : (
                      filteredNotifications.map((notification) => (
                        <NotificationItem
                          key={notification.id}
                          notification={notification}
                          onClick={handleMarkAsRead}
                        />
                      ))
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="comment" className="p-4 sm:p-6 lg:p-8">
                  <div className="mb-4 sm:mb-6">
                    <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-primary to-primary-600 bg-clip-text text-transparent">
                      Comment Notifications
                    </h2>
                    <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                      Comments and replies on your campaigns
                    </p>
                  </div>

                  <div className="space-y-2">
                    {filteredNotifications.length === 0 ? (
                      <div className="text-center py-8 sm:py-12">
                        <MessageSquare className="w-12 h-12 sm:w-16 sm:h-16 text-muted-foreground mx-auto mb-3 sm:mb-4" />
                        <h3 className="text-base sm:text-lg font-semibold text-muted-foreground mb-2">
                          No comment notifications
                        </h3>
                        <p className="text-xs sm:text-sm text-muted-foreground">
                          You'll see comments here when people engage with your
                          campaigns.
                        </p>
                      </div>
                    ) : (
                      filteredNotifications.map((notification) => (
                        <NotificationItem
                          key={notification.id}
                          notification={notification}
                          onClick={handleMarkAsRead}
                        />
                      ))
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="system" className="p-4 sm:p-6 lg:p-8">
                  <div className="mb-4 sm:mb-6">
                    <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-primary to-primary-600 bg-clip-text text-transparent">
                      System Notifications
                    </h2>
                    <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                      Important updates about your campaigns and account
                    </p>
                  </div>

                  <div className="space-y-2">
                    {filteredNotifications.length === 0 ? (
                      <div className="text-center py-8 sm:py-12">
                        <AlertCircle className="w-12 h-12 sm:w-16 sm:h-16 text-muted-foreground mx-auto mb-3 sm:mb-4" />
                        <h3 className="text-base sm:text-lg font-semibold text-muted-foreground mb-2">
                          No system notifications
                        </h3>
                        <p className="text-xs sm:text-sm text-muted-foreground">
                          System updates will appear here when available.
                        </p>
                      </div>
                    ) : (
                      filteredNotifications.map((notification) => (
                        <NotificationItem
                          key={notification.id}
                          notification={notification}
                          onClick={handleMarkAsRead}
                        />
                      ))
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-primary/5">
      <NavBar />
      <div className="container mx-auto px-4 pt-20 sm:pt-24 pb-16">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="text-slate-600 hover:text-slate-900 hover:bg-slate-100"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <PageHeader
              title="Notifications"
              description="Stay updated with your latest activities and interactions"
            />
          </motion.div>

          <Card className="bg-white/95 backdrop-blur-sm border-0 shadow-2xl rounded-2xl overflow-hidden mt-6 sm:mt-8">
            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className="w-full"
            >
              <div className="bg-gradient-to-r from-gray-50 to-blue-50/50 border-b border-gray-100">
                <TabsList className="w-full bg-transparent border-0 p-0 h-auto rounded-none overflow-x-auto">
                  <TabsTrigger
                    value="all"
                    className="flex-1 py-3 sm:py-4 lg:py-6 px-4 sm:px-6 lg:px-8 text-sm sm:text-base lg:text-lg font-semibold data-[state=active]:bg-white data-[state=active]:shadow-lg data-[state=active]:border-b-4 data-[state=active]:border-primary-500 rounded-none data-[state=active]:text-primary-600 whitespace-nowrap flex-shrink-0"
                  >
                    <Inbox className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                    All
                  </TabsTrigger>
                  <TabsTrigger
                    value="unread"
                    className="flex-1 py-3 sm:py-4 lg:py-6 px-4 sm:px-6 lg:px-8 text-sm sm:text-base lg:text-lg font-semibold data-[state=active]:bg-white data-[state=active]:shadow-lg data-[state=active]:border-b-4 data-[state=active]:border-primary-500 rounded-none data-[state=active]:text-primary-600 whitespace-nowrap flex-shrink-0"
                  >
                    <Bell className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                    Unread
                    {unreadCount > 0 && (
                      <Badge className="ml-1 sm:ml-2 bg-primary text-white border-0 text-xs">
                        {unreadCount}
                      </Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger
                    value="comment"
                    className="flex-1 py-3 sm:py-4 lg:py-6 px-4 sm:px-6 lg:px-8 text-sm sm:text-base lg:text-lg font-semibold data-[state=active]:bg-white data-[state=active]:shadow-lg data-[state=active]:border-b-4 data-[state=active]:border-primary-500 rounded-none data-[state=active]:text-primary-600 whitespace-nowrap flex-shrink-0"
                  >
                    <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                    Comments
                  </TabsTrigger>
                  <TabsTrigger
                    value="system"
                    className="flex-1 py-3 sm:py-4 lg:py-6 px-4 sm:px-6 lg:px-8 text-sm sm:text-base lg:text-lg font-semibold data-[state=active]:bg-white data-[state=active]:shadow-lg data-[state=active]:border-b-4 data-[state=active]:border-primary-500 rounded-none data-[state=active]:text-primary-600 whitespace-nowrap flex-shrink-0"
                  >
                    <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                    System
                  </TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="all" className="p-4 sm:p-6 lg:p-8">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
                  <div>
                    <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-primary to-primary-600 bg-clip-text text-transparent">
                      All Notifications
                    </h2>
                    <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                      {notifications?.length || 0} total notifications
                    </p>
                  </div>
                  {unreadCount > 0 && (
                    <Button
                      onClick={handleMarkAllAsRead}
                      variant="outline"
                      className="hover:bg-primary/10 hover:border-primary/20 text-xs sm:text-sm"
                    >
                      <CheckCheck className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                      <span className="hidden sm:inline">Mark All as Read</span>
                      <span className="sm:hidden">Mark All Read</span>
                    </Button>
                  )}
                </div>

                <div className="space-y-2">
                  {filteredNotifications.length === 0 ? (
                    <div className="text-center py-8 sm:py-12">
                      <Inbox className="w-12 h-12 sm:w-16 sm:h-16 text-muted-foreground mx-auto mb-3 sm:mb-4" />
                      <h3 className="text-base sm:text-lg font-semibold text-muted-foreground mb-2">
                        No notifications
                      </h3>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        You're all caught up! Check back later for new updates.
                      </p>
                    </div>
                  ) : (
                    filteredNotifications.map((notification) => (
                      <NotificationItem
                        key={notification.id}
                        notification={notification}
                        onClick={handleMarkAsRead}
                      />
                    ))
                  )}
                </div>
              </TabsContent>

              <TabsContent value="unread" className="p-4 sm:p-6 lg:p-8">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
                  <div>
                    <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-primary to-primary-600 bg-clip-text text-transparent">
                      Unread Notifications
                    </h2>
                    <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                      {unreadCount} unread notifications
                    </p>
                  </div>
                  {unreadCount > 0 && (
                    <Button
                      onClick={handleMarkAllAsRead}
                      className="bg-primary hover:bg-primary/90 text-xs sm:text-sm"
                    >
                      <CheckCheck className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                      <span className="hidden sm:inline">Mark All as Read</span>
                      <span className="sm:hidden">Mark All Read</span>
                    </Button>
                  )}
                </div>

                <div className="space-y-2">
                  {filteredNotifications.length === 0 ? (
                    <div className="text-center py-8 sm:py-12">
                      <CheckCircle className="w-12 h-12 sm:w-16 sm:h-16 text-primary mx-auto mb-3 sm:mb-4" />
                      <h3 className="text-base sm:text-lg font-semibold text-muted-foreground mb-2">
                        All caught up!
                      </h3>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        You have no unread notifications.
                      </p>
                    </div>
                  ) : (
                    filteredNotifications.map((notification) => (
                      <NotificationItem
                        key={notification.id}
                        notification={notification}
                        onClick={handleMarkAsRead}
                      />
                    ))
                  )}
                </div>
              </TabsContent>

              <TabsContent value="comment" className="p-4 sm:p-6 lg:p-8">
                <div className="mb-4 sm:mb-6">
                  <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-primary to-primary-600 bg-clip-text text-transparent">
                    Comment Notifications
                  </h2>
                  <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                    Comments and replies on your campaigns
                  </p>
                </div>

                <div className="space-y-2">
                  {filteredNotifications.length === 0 ? (
                    <div className="text-center py-8 sm:py-12">
                      <MessageSquare className="w-12 h-12 sm:w-16 sm:h-16 text-muted-foreground mx-auto mb-3 sm:mb-4" />
                      <h3 className="text-base sm:text-lg font-semibold text-muted-foreground mb-2">
                        No comment notifications
                      </h3>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        You'll see comments here when people engage with your
                        campaigns.
                      </p>
                    </div>
                  ) : (
                    filteredNotifications.map((notification) => (
                      <NotificationItem
                        key={notification.id}
                        notification={notification}
                        onClick={handleMarkAsRead}
                      />
                    ))
                  )}
                </div>
              </TabsContent>

              <TabsContent value="system" className="p-4 sm:p-6 lg:p-8">
                <div className="mb-4 sm:mb-6">
                  <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-primary to-primary-600 bg-clip-text text-transparent">
                    System Notifications
                  </h2>
                  <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                    Important updates about your campaigns and account
                  </p>
                </div>

                <div className="space-y-2">
                  {filteredNotifications.length === 0 ? (
                    <div className="text-center py-8 sm:py-12">
                      <AlertCircle className="w-12 h-12 sm:w-16 sm:h-16 text-muted-foreground mx-auto mb-3 sm:mb-4" />
                      <h3 className="text-base sm:text-lg font-semibold text-muted-foreground mb-2">
                        No system notifications
                      </h3>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        System updates will appear here when available.
                      </p>
                    </div>
                  ) : (
                    filteredNotifications.map((notification) => (
                      <NotificationItem
                        key={notification.id}
                        notification={notification}
                        onClick={handleMarkAsRead}
                      />
                    ))
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default UserNotifications;
